import { useCallback, useRef, useState } from 'react';
import { Sparkles, Settings, ChevronDown, Copy, Check, BotMessageSquare, Package } from 'lucide-react';
import { useAnalytics } from '../../../core/analytics/useAnalytics';
import { cn } from '../../../lib/utils';
import { writeClipboardText } from '../../../lib/clipboard';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';
import { useAiToolbarStatus } from '../hooks/useAiToolbarStatus';
import { useAiWorkspaceSetupConsent } from '../hooks/useAiWorkspaceSetupConsent';
import { useTerminalStore } from '../../terminal/store/terminalStore';
import { useConnectionStore } from '../../connections/store';
import { useDatabaseUIStore } from '../../database-objects/store';
import { createTerminalSession } from '../../terminal/services/terminalApi';
import { isBrowserMode, useRestrictedFeatureDialog } from '../../browser-mode';
import type { TerminalSession } from '../../../types/terminal';
import type { ActiveConnection } from '../../connections/models/connectionTypes';
import { useClaudeCliDetection } from '../../ai-chat/hooks/useClaudeCliDetection';
import { AiChatCliRequiredDialog } from '../../ai-chat/components/AiChatCliRequiredDialog';
import { AiWorkspaceSetupConfirmDialog } from './AiWorkspaceSetupConfirmDialog';
import { safeConnectionName } from '../../../types/safeConnectionDisplay';

type AiToolbarButtonProps = {
  onOpenAiSettings: () => void;
  /** Open a new query editor tab with AI sidebar already open */
  onNewQueryWithAi?: () => void;
  /** Open the Package Explorer with AI sidebar already open (Oracle only) */
  onNewPackageExplorerWithAi?: () => void;
  /** Whether the selected connection is Oracle */
  isOracleConnection?: boolean;
};

/**
 * AI Toolbar Button
 *
 * Dropdown button for quick access to AI features:
 * - Open AI Workspace terminal
 * - Open AI Settings
 * - MCP Status indicator
 */
export function AiToolbarButton({ onOpenAiSettings, onNewQueryWithAi, onNewPackageExplorerWithAi, isOracleConnection }: AiToolbarButtonProps) {
  const { track } = useAnalytics();
  const { mcpRunning, mcpEndpoint, isLoading } = useAiToolbarStatus();
  const [copied, setCopied] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [showCliRequiredDialog, setShowCliRequiredDialog] = useState(false);
  const skipFocusRestoreRef = useRef(false);
  const restrictedDialog = useRestrictedFeatureDialog();
  const { checkAndOpenSidebar } = useClaudeCliDetection();
  const mcpAddress = mcpEndpoint
    ? mcpEndpoint.replace(/^https?:\/\//, '').replace(/\/mcp$/, '')
    : undefined;

  // Terminal store
  const sessions = useTerminalStore((s) => s.sessions);
  const showPanel = useTerminalStore((s) => s.showPanel);
  const setActiveSession = useTerminalStore((s) => s.setActiveSession);
  const addSession = useTerminalStore((s) => s.addSession);

  // Connection state
  const activeConnections = useConnectionStore((s) => s.activeConnections);
  const selectedConnectionId = useDatabaseUIStore((s) => s.selectedConnectionId);
  const selectedDatabase = useDatabaseUIStore((s) => s.selectedDatabase);

  // Get the selected connection
  const getSelectedConnection = useCallback((): ActiveConnection | null => {
    const connectedConnections = activeConnections.filter((c) => c.status === 'connected');
    if (selectedConnectionId) {
      return connectedConnections.find((c) => c.id === selectedConnectionId) ?? null;
    }
    // Fall back to first active connection if nothing selected
    return connectedConnections[0] ?? null;
  }, [selectedConnectionId, activeConnections]);

  // Find existing AI terminal session for current context
  const findExistingAiSession = useCallback(() => {
    const connection = getSelectedConnection();
    if (!connection) return null;

    // Look for an AI session with matching connection and database
    return sessions.find(
      (s) =>
        s.type === 'ai' &&
        s.connectionId === connection.id &&
        s.database === selectedDatabase
    ) ?? null;
  }, [sessions, getSelectedConnection, selectedDatabase]);

  // Create AI terminal with specific connection and database
  const createAiTerminal = useCallback(async (
    connection: ActiveConnection,
    database: string | undefined
  ) => {
    try {
      const result = await createTerminalSession({
        type: 'ai',
        connectionId: connection.id,
        database,
      });

      if (result.success && result.sessionId) {
        const connInfo = connection.connection;
        const session: TerminalSession = {
          id: result.sessionId,
          title: database
            ? `AI: ${safeConnectionName(connInfo)} - ${database}`
            : `AI: ${safeConnectionName(connInfo)}`,
          type: 'ai',
          connectionId: connection.id,
          database,
          workspacePath: result.workspacePath,
          createdAt: Date.now(),
          // Only seed an optimistic "Initializing…" state when the main
          // process actually scheduled a schema export for this session —
          // otherwise no `terminal:init-progress` event will ever arrive to
          // move it out of 'starting', leaving the context/status bars stuck
          // forever (e.g. mock engine, exportSchemaOnOpen off, unsupported
          // engine, no database). Leaving initState undefined shows the
          // normal ready state immediately instead.
          initState: result.schemaExportScheduled
            ? { phase: 'starting', message: 'Initializing AI workspace...' }
            : undefined,
        };
        addSession(session);
        return session.id;
      }
      return null;
    } catch (err) {
      console.error('[AiToolbarButton] Error creating AI terminal:', err);
      return null;
    }
  }, [addSession]);

  // Request AI terminal (triggers connection selector flow if needed)
  const requestAiTerminal = useTerminalStore((s) => s.requestAiTerminal);

  // Shared AI-workspace consent gate (also used by TerminalPanel's own
  // AI-terminal creation surfaces — see useAiWorkspaceSetupConsent). Behind
  // `confirmBeforeSetup`, requestSetup opens AiWorkspaceSetupConfirmDialog
  // (rendered below) and defers this creation until the user confirms; with
  // the setting off, creation runs immediately.
  const createAndShowAiTerminal = useCallback(
    async (connection: ActiveConnection, database: string | undefined) => {
      const sessionId = await createAiTerminal(connection, database);
      if (sessionId) {
        showPanel();
      }
    },
    [createAiTerminal, showPanel]
  );
  const {
    consentContext,
    requestSetup: requestAiWorkspaceSetup,
    handleConfirm: handleConsentConfirm,
    handleCancel: handleConsentCancel,
  } = useAiWorkspaceSetupConsent(createAndShowAiTerminal);

  // Handle opening AI Workspace
  const handleOpenAiWorkspace = useCallback(() => {
    // Close dropdown first
    setDropdownOpen(false);

    // Check browser mode restriction
    if (restrictedDialog.checkAndBlock('terminal')) return;

    // Check for existing session with same context
    const existingSession = findExistingAiSession();
    if (existingSession) {
      // Focus existing session
      setActiveSession(existingSession.id);
      showPanel();
      return;
    }

    // Get current connection context
    const connection = getSelectedConnection();
    if (!connection) {
      // No connection selected - request AI terminal (will trigger connection selector in TerminalPanel)
      requestAiTerminal();
      return;
    }

    // Consent gate — unless the user opted out, confirm (with a target-database
    // choice + an honest "what gets exported" explanation) BEFORE any export
    // side-effect begins. Existing-session reuse above never reaches here (it
    // only focuses, it doesn't export), so the gate sits exactly at the create
    // path.
    requestAiWorkspaceSetup(connection, selectedDatabase);
  }, [
    restrictedDialog,
    findExistingAiSession,
    setActiveSession,
    showPanel,
    getSelectedConnection,
    selectedDatabase,
    requestAiTerminal,
    requestAiWorkspaceSetup,
  ]);

  // Handle copying MCP endpoint
  const handleCopyEndpoint = useCallback(() => {
    if (mcpEndpoint) {
      void writeClipboardText(mcpEndpoint);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [mcpEndpoint]);

  // Determine button styling based on MCP status
  const getStatusIndicatorClass = () => {
    if (isLoading) return 'bg-gray-400';
    if (mcpRunning) return 'bg-green-500';
    return 'bg-gray-400';
  };

  // Handle "New Query Editor with AI" — validates CLI before opening
  const handleNewQueryWithAi = useCallback(() => {
    skipFocusRestoreRef.current = true;
    setDropdownOpen(false);
    checkAndOpenSidebar(
      () => onNewQueryWithAi?.(),
      () => setShowCliRequiredDialog(true)
    );
  }, [checkAndOpenSidebar, onNewQueryWithAi]);

  // Handle "Package Explorer with AI" — validates CLI before opening
  const handleNewPackageExplorerWithAi = useCallback(() => {
    skipFocusRestoreRef.current = true;
    setDropdownOpen(false);
    checkAndOpenSidebar(
      () => onNewPackageExplorerWithAi?.(),
      () => setShowCliRequiredDialog(true)
    );
  }, [checkAndOpenSidebar, onNewPackageExplorerWithAi]);

  // Handle opening AI Settings - close dropdown first to avoid overlay conflicts
  const handleOpenAiSettingsClick = useCallback(() => {
    setDropdownOpen(false);
    // Small delay to ensure dropdown fully closes before dialog opens
    setTimeout(() => {
      onOpenAiSettings();
    }, 0);
  }, [onOpenAiSettings]);

  return (<>
    <DropdownMenu
      open={dropdownOpen}
      onOpenChange={(open) => {
        setDropdownOpen(open);
        if (open) track('toolbar:ai_menu_opened');
      }}
    >
      <DropdownMenuTrigger asChild>
        <button
          className="group relative flex items-center gap-1.5 px-2 py-1 text-sm rounded-sm hover:bg-muted transition-[color,background-color,scale] active:scale-[0.96] before:absolute before:inset-x-0 before:-inset-y-1.5 before:content-['']"
          title="AI Tools"
          data-toolbar-item
          data-testid="ai-toolbar-button"
        >
          <Sparkles className="h-4 w-4" />
          <span>AI</span>
          <ChevronDown className="h-3 w-3 transition-transform duration-200 group-data-[state=open]:rotate-180" />
          {/* MCP status indicator dot */}
          <span
            className={`absolute top-0.5 right-0.5 w-2 h-2 rounded-full transition-colors ${getStatusIndicatorClass()}`}
            title={mcpRunning ? 'MCP Server Running' : 'MCP Server Not Running'}
          />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="w-64"
        onCloseAutoFocus={(e) => {
          if (skipFocusRestoreRef.current) {
            e.preventDefault();
            skipFocusRestoreRef.current = false;
          }
        }}
      >
        <DropdownMenuItem
          onClick={handleNewQueryWithAi}
          disabled={isBrowserMode()}
          data-testid="new-query-with-ai-menu-item"
        >
          <BotMessageSquare className="h-4 w-4 mr-2" />
          <div className="flex flex-col">
            <span>AI Query Editor</span>
            <span className="text-xs text-muted-foreground">
              New query tab with AI Chat
            </span>
          </div>
          {isBrowserMode() && (
            <span className="ml-auto text-xs text-muted-foreground">
              Desktop only
            </span>
          )}
        </DropdownMenuItem>

        {isOracleConnection && (
          <DropdownMenuItem
            onClick={handleNewPackageExplorerWithAi}
            disabled={isBrowserMode()}
            data-testid="new-package-explorer-with-ai-menu-item"
          >
            <Package className="h-4 w-4 mr-2" />
            <div className="flex flex-col">
              <span>AI Package Explorer</span>
              <span className="text-xs text-muted-foreground">
                Browse packages with AI Chat
              </span>
            </div>
            {isBrowserMode() && (
              <span className="ml-auto text-xs text-muted-foreground">
                Desktop only
              </span>
            )}
          </DropdownMenuItem>
        )}

        <DropdownMenuItem
          onClick={handleOpenAiWorkspace}
          disabled={isBrowserMode()}
          data-testid="ai-workspace-menu-item"
        >
          <Sparkles className="h-4 w-4 mr-2" />
          <div className="flex flex-col">
            <span>AI Workspace</span>
            <span className="text-xs text-muted-foreground">
              {getContextLabel()}
            </span>
          </div>
          {isBrowserMode() && (
            <span className="ml-auto text-xs text-muted-foreground">
              Desktop only
            </span>
          )}
        </DropdownMenuItem>

        <DropdownMenuItem
          onClick={handleOpenAiSettingsClick}
          data-testid="ai-settings-menu-item"
        >
          <Settings className="h-4 w-4 mr-2" />
          <div className="flex flex-col">
            <span>AI Settings & Tips</span>
            <span className="text-xs text-muted-foreground">
              {mcpRunning ? 'MCP Enabled' : 'MCP Disabled'}
            </span>
          </div>
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        {/* MCP Status Row */}
        <div
          className="flex items-center justify-between px-2 py-1.5 text-sm cursor-pointer hover:bg-muted/50 rounded-sm"
          onClick={mcpEndpoint ? handleCopyEndpoint : undefined}
          title={mcpEndpoint ? 'Click to copy endpoint URL' : undefined}
          data-testid="mcp-status-row"
        >
          <div className="flex items-center gap-2">
            <span
              className={`w-2 h-2 rounded-full transition-colors ${getStatusIndicatorClass()}`}
            />
            <span className="text-muted-foreground">
              {mcpRunning && mcpAddress ? `MCP: ${mcpAddress}` : 'MCP: Disabled'}
            </span>
          </div>
          {mcpEndpoint && (
            <button className="text-muted-foreground hover:text-foreground">
              {/* Both icons stay mounted and cross-fade on copy so the swap
                  animates (and reverses when the copied state expires). */}
              <span className="relative block h-3.5 w-3.5">
                <Copy
                  className={cn(
                    'absolute inset-0 h-3.5 w-3.5 transition-[opacity,scale,filter] duration-300 ease-[cubic-bezier(0.2,0,0,1)]',
                    copied ? 'opacity-0 scale-[0.25] blur-[4px]' : 'opacity-100 scale-100 blur-[0px]',
                  )}
                />
                <Check
                  className={cn(
                    'absolute inset-0 h-3.5 w-3.5 text-green-500 transition-[opacity,scale,filter] duration-300 ease-[cubic-bezier(0.2,0,0,1)]',
                    copied ? 'opacity-100 scale-100 blur-[0px]' : 'opacity-0 scale-[0.25] blur-[4px]',
                  )}
                />
              </span>
            </button>
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>

    <AiChatCliRequiredDialog
      open={showCliRequiredDialog}
      onOpenChange={setShowCliRequiredDialog}
    />

    {consentContext && (
      <AiWorkspaceSetupConfirmDialog
        open
        connection={consentContext.connection}
        initialDatabase={consentContext.database}
        onConfirm={handleConsentConfirm}
        onCancel={handleConsentCancel}
      />
    )}
  </>
  );

  // Helper to get context label for AI Workspace menu item
  function getContextLabel() {
    const connection = getSelectedConnection();
    if (!connection) {
      return 'Select a connection to start';
    }
    const connName = safeConnectionName(connection.connection);
    if (selectedDatabase) {
      return `${connName} - ${selectedDatabase}`;
    }
    return connName;
  }
}
